import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogOut, Plus, Edit2, Trash2, X, Mail, CreditCard, MessageSquare, FileEdit, Home as HomeIcon, Info, User, Settings, BookOpen, Calendar, Users, Sparkles, MessageCircle, Video, Image as ImageIcon, Heart, Download, BarChart3, UserCheck, Scale, ShieldCheck, RotateCcw, Ban, FlaskConical, Wallet, Shield } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { PageContentEditor } from '../components/PageContentEditor';
import { SiteSettingsEditor } from '../components/SiteSettingsEditor';
import { ImageUpload } from '../components/ImageUpload';
import { PdfUpload } from '../components/PdfUpload';
import { AnalyticsDashboard } from '../components/AnalyticsDashboard';
import { RegistrationsAdmin } from '../components/RegistrationsAdmin';
import { FeeReceivable } from '../components/FeeReceivable';
import { UserManagement } from '../components/UserManagement';
import { KeyFeaturesInput } from '../components/KeyFeatures';
import { GalleryManagement } from '../components/GalleryManagement';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const apiErrorMessage = (error) => {
  const detail = error.response?.data?.detail;
  if (Array.isArray(detail)) {
    return 'Please fix: ' + detail.map((d) => `${(d.loc || []).slice(-1)[0] || 'field'} — ${d.msg}`).join('; ');
  }
  if (typeof detail === 'string') return detail;
  if (!error.response) return 'Network error — could not reach the server. Please try again.';
  return `Save failed (error ${error.response.status}). Please try again.`;
};

const CONTENT_TABS = [
  { id: 'page-home', label: 'Home', icon: HomeIcon, kind: 'page', pageId: 'home' },
  { id: 'page-about', label: 'About Us', icon: Info, kind: 'page', pageId: 'about' },
  { id: 'page-mentor', label: 'Our Mentor', icon: User, kind: 'page', pageId: 'mentor' },
  { id: 'page-gallery', label: 'Gallery Page', icon: ImageIcon, kind: 'page', pageId: 'gallery' },
  { id: 'page-terms', label: 'Terms & Conditions', icon: Scale, kind: 'page', pageId: 'terms' },
  { id: 'page-privacy', label: 'Privacy Policy', icon: ShieldCheck, kind: 'page', pageId: 'privacy' },
  { id: 'page-refund', label: 'Refund Policy', icon: RotateCcw, kind: 'page', pageId: 'refund' },
  { id: 'page-cancellation', label: 'Cancellation Policy', icon: Ban, kind: 'page', pageId: 'cancellation' },
  { id: 'page-publications', label: 'Publications Page', icon: FlaskConical, kind: 'page', pageId: 'publications' },
  { id: 'courses', label: 'Courses', icon: BookOpen, kind: 'collection' },
  { id: 'events', label: 'Events', icon: Calendar, kind: 'collection' },
  { id: 'satsangs', label: 'Satsangs', icon: Sparkles, kind: 'collection' },
  { id: 'resources', label: 'Articles', icon: FileEdit, kind: 'collection' },
  { id: 'videos', label: 'Videos', icon: Video, kind: 'collection' },
  { id: 'testimonials', label: 'Testimonials', icon: MessageCircle, kind: 'collection' },
  { id: 'publications', label: 'Publications', icon: FlaskConical, kind: 'collection' },
  { id: 'gallery-media', label: 'Gallery Media', icon: ImageIcon, kind: 'gallery' },
];

const COMMUNITY_TABS = [
  { id: 'analytics', label: 'Analytics', icon: BarChart3, perm: 'analytics' },
  { id: 'registrations', label: 'Event Registrations', icon: UserCheck, perm: 'event_registrations' },
  { id: 'enrolments', label: 'Course Enrolments', icon: CreditCard, perm: 'course_enrolments' },
  { id: 'fee-receivable', label: 'Fee Receivable', icon: Wallet, perm: 'fee_receivable' },
  { id: 'donations', label: 'Donations', icon: Heart, perm: 'donations' },
  { id: 'subscribers', label: 'Newsletter', icon: Mail, perm: 'newsletter' },
  { id: 'contacts', label: 'Inquiries', icon: MessageSquare, perm: 'inquiries' },
  { id: 'users', label: 'Users', icon: Shield, perm: null, adminOnly: true },
  { id: 'settings', label: 'Settings', icon: Settings, perm: 'settings' },
];


const PricingModeInput = ({ formData, setFormData }) => {
  const mode = formData.pricing_mode || 'free';
  const feeInr = ((formData.fee_amount || 0) / 100) || '';
  const modes = [
    { id: 'free', label: 'Free', desc: 'No charge — participants just register' },
    { id: 'fee', label: 'Fee', desc: 'Fixed amount — required to register' },
    { id: 'dakshina', label: 'Dakshina', desc: 'Participant chooses their offering' },
  ];
  return (
    <div className="bg-cream/50 border border-charcoal-100 rounded-lg p-4 space-y-3">
      <div className="text-sm font-body font-semibold text-charcoal-700">Registration Pricing</div>
      <div className="grid grid-cols-3 gap-2">
        {modes.map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => setFormData({ ...formData, pricing_mode: m.id, fee_amount: m.id === 'fee' ? formData.fee_amount || 0 : 0 })}
            data-testid={`pricing-mode-${m.id}`}
            className={`rounded-lg p-3 text-sm border transition-colors ${
              mode === m.id
                ? 'bg-saffron-600 text-white border-saffron-600'
                : 'bg-white text-charcoal-700 border-charcoal-200 hover:border-saffron-400'
            }`}
          >
            <div className="font-semibold">{m.label}</div>
            <div className={`text-[10px] mt-1 ${mode === m.id ? 'text-white/80' : 'text-charcoal-500'}`}>
              {m.desc}
            </div>
          </button>
        ))}
      </div>
      {mode === 'fee' && (
        <div>
          <label className="block text-xs font-medium text-charcoal-600 mb-1">Fee Amount (₹) *</label>
          <input
            type="number"
            min="1"
            required
            value={feeInr}
            data-testid="pricing-fee-amount"
            onChange={(e) => setFormData({ ...formData, fee_amount: Math.round(parseFloat(e.target.value || '0') * 100) })}
            placeholder="e.g., 500"
            className="w-full px-4 py-2.5 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500"
          />
        </div>
      )}
    </div>
  );
};


const TabBar = ({ tabs, activeTab, onSelect, testIdPrefix }) => {
  const containerRef = useRef(null);
  const [indicator, setIndicator] = useState({ left: 0, width: 0, opacity: 0 });

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const activeEl = container.querySelector(`[data-tab-id="${activeTab}"]`);
    if (activeEl) {
      const containerRect = container.getBoundingClientRect();
      const activeRect = activeEl.getBoundingClientRect();
      setIndicator({
        left: activeRect.left - containerRect.left + container.scrollLeft,
        width: activeRect.width,
        opacity: 1,
      });
    }
  }, [activeTab, tabs]);

  return (
    <div className="relative bg-white rounded-xl shadow-md border border-charcoal-100 overflow-hidden">
      <div ref={containerRef} className="flex overflow-x-auto relative">
        <motion.div
          className="absolute bottom-0 h-1 bg-gradient-to-r from-saffron-600 to-gold-500 rounded-full"
          animate={indicator}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        />
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              data-tab-id={tab.id}
              data-testid={`${testIdPrefix}-${tab.id}`}
              onClick={() => onSelect(tab)}
              className={`flex items-center gap-2 px-5 py-4 font-body font-medium transition-colors whitespace-nowrap ${
                isActive ? 'text-saffron-600' : 'text-charcoal-600 hover:text-charcoal-800'
              }`}
            >
              <Icon size={16} /> {tab.label}
              {tab.count !== undefined && (
                <span className={`text-xs px-2 py-0.5 rounded-full ${isActive ? 'bg-saffron-100 text-saffron-700' : 'bg-charcoal-100 text-charcoal-600'}`}>
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const Admin = () => {
  const [group, setGroup] = useState('content');
  const [activeTab, setActiveTab] = useState('page-home');
  const [courses, setCourses] = useState([]);
  const [events, setEvents] = useState([]);
  const [satsangs, setSatsangs] = useState([]);
  const [resources, setResources] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [videos, setVideos] = useState([]);
  const [publications, setPublications] = useState([]);
  const [payments, setPayments] = useState([]);
  const [courseEnrolments, setCourseEnrolments] = useState([]);
  const [donations, setDonations] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const isAdmin = user && ['super_admin', 'state_admin', 'admin'].includes(user.role);
    if (user && !isAdmin) {
      navigate('/');
      return;
    }
    if (isAdmin) {
      fetchAll();
    }
  }, [user, navigate]);

  const fetchAll = async () => {
    try {
      const config = { withCredentials: true };
      const results = await Promise.allSettled([
        axios.get(`${API_URL}/api/courses?include_all=true`),
        axios.get(`${API_URL}/api/events?include_all=true`),
        axios.get(`${API_URL}/api/satsangs?include_all=true`),
        axios.get(`${API_URL}/api/resources`),
        axios.get(`${API_URL}/api/testimonials`),
        axios.get(`${API_URL}/api/videos`),
        axios.get(`${API_URL}/api/publications`),
        axios.get(`${API_URL}/api/payments`, config),
        axios.get(`${API_URL}/api/donations`, config),
        axios.get(`${API_URL}/api/admin/newsletter`, config),
        axios.get(`${API_URL}/api/admin/contacts`, config),
      ]);
      const setters = [setCourses, setEvents, setSatsangs, setResources, setTestimonials, setVideos, setPublications, setPayments, setDonations, setSubscribers, setContacts];
      results.forEach((res, i) => { if (res.status === 'fulfilled' && Array.isArray(res.value.data)) setters[i](res.value.data); });
      // Course enrolments now come from the unified registrations collection.
      try {
        const grouped = await axios.get(`${API_URL}/api/registrations/grouped`, { withCredentials: true });
        setCourseEnrolments(grouped.data.filter((g) => g.kind === 'course'));
      } catch {
        setCourseEnrolments([]);
      }
    } catch (error) {
      // Silent — dashboard degrades gracefully if any list fails to load
    }
  };

  const handleLogout = async () => {
    // Navigate away from /admin BEFORE clearing auth state, otherwise
    // ProtectedRoute sees user=null while still mounted on /admin and
    // redirects to /login for a brief flash.
    navigate('/', { replace: true });
    await logout();
  };

  const getDataForCollection = (id) => {
    return { courses, events, satsangs, resources, testimonials, videos, publications }[id] || [];
  };

  const openModal = (type, item = null) => {
    setEditingItem(item);
    if (item) {
      setFormData(item);
    } else {
      const defaults = {
        courses: { title: '', description: '', category: '', image_url: '', duration: '', level: '', key_features: [], price: 0, preview_video_url: '', pricing_mode: 'free', fee_amount: 0, allow_partial_payment: false, min_partial_amount: 0 },
        events: { title: '', description: '', event_date: '', event_time: '', location: '', image_url: '', is_online: false, duration: '', level: '', key_features: [], pricing_mode: 'free', fee_amount: 0, allow_partial_payment: false, min_partial_amount: 0 },
        satsangs: { title: '', description: '', date: '', time: '', speaker: '', image_url: '', pricing_mode: 'free', fee_amount: 0 },
        resources: { title: '', content: '', category: '', image_url: '' },
        testimonials: { name: '', role: '', quote: '', image_url: '', rating: 5, is_featured: false },
        videos: { title: '', description: '', category: '', video_url: '', thumbnail_url: '' },
        publications: { title: '', category: 'Chakra Energy', lead_guidance: '', team_members: [], publish_date: '', abstract: '', scriptural_reference: '', findings: '', pdf_url: '', thumbnail_url: '', is_featured: false },
      };
      setFormData(defaults[type] || {});
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormData({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const setter = { courses: setCourses, events: setEvents, satsangs: setSatsangs, resources: setResources, testimonials: setTestimonials, videos: setVideos, publications: setPublications }[activeTab];
      if (editingItem) {
        const { data } = await axios.put(`${API_URL}/api/${activeTab}/${editingItem.id}`, formData, { withCredentials: true });
        setter?.((list) => list.map((x) => (x.id === editingItem.id ? { ...x, ...data } : x)));
        toast.success('Updated successfully');
      } else {
        const { data } = await axios.post(`${API_URL}/api/${activeTab}`, formData, { withCredentials: true });
        if (data?.id) setter?.((list) => [data, ...list]);
        toast.success('Created successfully');
      }
      closeModal();
      fetchAll();
    } catch (error) {
      toast.error(apiErrorMessage(error), { duration: 8000 });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await axios.delete(`${API_URL}/api/${activeTab}/${id}`, { withCredentials: true });
      toast.success('Deleted successfully');
      fetchAll();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const handleUnsubscribe = async (email) => {
    if (!window.confirm(`Remove ${email} from newsletter?`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/newsletter/${encodeURIComponent(email)}`, { withCredentials: true });
      toast.success('Subscriber removed');
      fetchAll();
    } catch (error) {
      toast.error('Remove failed');
    }
  };

  const totalRevenue = payments.filter((p) => p.status === 'completed').reduce((sum, p) => sum + (p.amount || 0), 0);

  const contentTabsWithCount = CONTENT_TABS.map((t) => ({
    ...t,
    count: t.kind === 'collection' ? getDataForCollection(t.id).length : undefined,
  }));

  // Filter community tabs by user's tab_permissions matrix (super_admin sees all).
  const isSuperAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  const perms = user?.tab_permissions || {};
  const communityTabsWithCount = COMMUNITY_TABS
    .filter((t) => {
      if (isSuperAdmin) return true;
      if (t.adminOnly) return user?.role === 'state_admin';
      return t.perm ? perms[t.perm] === 1 : true;
    })
    .map((t) => {
      if (t.id === 'payments') return { ...t, count: payments.length };
      if (t.id === 'subscribers') return { ...t, count: subscribers.length };
      if (t.id === 'contacts') return { ...t, count: contacts.length };
      return t;
    });

  const activeTabDef = [...CONTENT_TABS, ...COMMUNITY_TABS].find((t) => t.id === activeTab);
  const isPageTab = activeTabDef?.kind === 'page';
  const isCollectionTab = activeTabDef?.kind === 'collection';
  const currentCollection = isCollectionTab ? getDataForCollection(activeTab) : [];

  return (
    <div className="min-h-screen pt-20 bg-cream" data-testid="admin-page">
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-heading font-bold text-charcoal-800">Admin Dashboard</h1>
            <p className="text-charcoal-600 mt-1">Manage content, community, and site settings</p>
          </div>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-button"
            className="flex items-center gap-2 bg-charcoal-800 text-white rounded-lg px-4 py-2 hover:bg-charcoal-700 transition-colors"
          >
            <LogOut size={18} /> Logout
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-5 border border-charcoal-100">
            <div className="text-sm text-charcoal-500 font-body mb-1">Courses</div>
            <div className="text-2xl font-heading font-bold text-charcoal-800">{courses.length}</div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-charcoal-100">
            <div className="text-sm text-charcoal-500 font-body mb-1">Course Enrolments</div>
            <div className="text-2xl font-heading font-bold text-charcoal-800">
              {courseEnrolments.reduce((sum, g) => sum + (g.registrations || 0), 0)}
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-charcoal-100">
            <div className="text-sm text-charcoal-500 font-body mb-1">Subscribers</div>
            <div className="text-2xl font-heading font-bold text-charcoal-800">{subscribers.length}</div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-saffron-200 bg-gradient-to-br from-saffron-50 to-gold-100">
            <div className="text-sm text-charcoal-500 font-body mb-1">Total Revenue</div>
            <div className="text-2xl font-heading font-bold text-saffron-700">
              ₹{(totalRevenue / 100).toLocaleString()}
            </div>
          </div>
        </div>

        <div className="flex gap-2 mb-4 border-b border-charcoal-200">
          <button
            onClick={() => { setGroup('content'); setActiveTab(CONTENT_TABS[0].id); }}
            data-testid="group-content"
            className={`px-6 py-3 font-heading text-lg transition-colors relative ${
              group === 'content' ? 'text-saffron-600' : 'text-charcoal-500 hover:text-charcoal-700'
            }`}
          >
            Content
            {group === 'content' && <motion.div layoutId="group-indicator" className="absolute bottom-[-1px] left-0 right-0 h-0.5 bg-saffron-600" />}
          </button>
          <button
            onClick={() => { setGroup('community'); setActiveTab(COMMUNITY_TABS[0].id); }}
            data-testid="group-community"
            className={`px-6 py-3 font-heading text-lg transition-colors relative ${
              group === 'community' ? 'text-saffron-600' : 'text-charcoal-500 hover:text-charcoal-700'
            }`}
          >
            Community
            {group === 'community' && <motion.div layoutId="group-indicator" className="absolute bottom-[-1px] left-0 right-0 h-0.5 bg-saffron-600" />}
          </button>
        </div>

        <div className="mb-6">
          <TabBar
            tabs={group === 'content' ? contentTabsWithCount : communityTabsWithCount}
            activeTab={activeTab}
            onSelect={(tab) => setActiveTab(tab.id)}
            testIdPrefix="admin-tab"
          />
        </div>

        {isPageTab && <PageContentEditor pageId={activeTabDef.pageId} />}

        {isCollectionTab && (
          <div className="bg-white rounded-xl shadow-md border border-charcoal-100 p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-heading text-charcoal-800">
                {activeTabDef.label}
              </h2>
              <button
                onClick={() => openModal(activeTab)}
                data-testid="admin-add-button"
                className="flex items-center gap-2 bg-saffron-600 text-white rounded-lg px-4 py-2 hover:bg-saffron-500 transition-colors"
              >
                <Plus size={18} /> Add New
              </button>
            </div>
            {currentCollection.length === 0 ? (
              <p className="text-center text-charcoal-500 py-12">No items found. Add your first one!</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-charcoal-100">
                      <th className="text-left py-3 px-4 font-body font-semibold text-charcoal-700">Title/Name</th>
                      <th className="text-left py-3 px-4 font-body font-semibold text-charcoal-700">Info</th>
                      <th className="text-right py-3 px-4 font-body font-semibold text-charcoal-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentCollection.map((item) => (
                      <tr key={item.id} className="border-b border-charcoal-50 hover:bg-cream/50">
                        <td className="py-3 px-4 font-body text-charcoal-800">{item.title || item.name}</td>
                        <td className="py-3 px-4 font-body text-charcoal-600 text-sm">
                          {item.category || item.speaker || item.event_date || item.date || item.role || 'N/A'}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <button
                            onClick={() => openModal(activeTab, item)}
                            className="text-saffron-600 hover:text-saffron-500 mr-3"
                            data-testid={`edit-button-${item.id}`}
                          >
                            <Edit2 size={18} />
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="text-red-600 hover:text-red-500"
                            data-testid={`delete-button-${item.id}`}
                          >
                            <Trash2 size={18} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'analytics' && (
          <AnalyticsDashboard />
        )}

        {activeTab === 'registrations' && (
          <RegistrationsAdmin kindFilter={['event', 'satsang']} />
        )}

        {activeTab === 'enrolments' && (
          <RegistrationsAdmin kindFilter="course" />
        )}
        {activeTab === 'fee-receivable' && <FeeReceivable />}

        {activeTab === 'payments' && (
          <div className="bg-white rounded-xl shadow-md border border-charcoal-100 p-6">
            <h2 className="text-xl font-heading text-charcoal-800 mb-6">Course Enrollments & Payments</h2>
            {payments.length === 0 ? (
              <p className="text-center text-charcoal-500 py-12">No enrollments yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-charcoal-100">
                      <th className="text-left py-3 px-4 font-body font-semibold text-charcoal-700">User</th>
                      <th className="text-left py-3 px-4 font-body font-semibold text-charcoal-700">Item</th>
                      <th className="text-left py-3 px-4 font-body font-semibold text-charcoal-700">Amount</th>
                      <th className="text-left py-3 px-4 font-body font-semibold text-charcoal-700">Status</th>
                      <th className="text-left py-3 px-4 font-body font-semibold text-charcoal-700">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((p) => (
                      <tr key={p.order_id} className="border-b border-charcoal-50">
                        <td className="py-3 px-4">
                          <div className="font-medium text-charcoal-800">{p.user_name}</div>
                          <div className="text-xs text-charcoal-500">{p.user_email}</div>
                        </td>
                        <td className="py-3 px-4 text-sm text-charcoal-600">{p.item_type} - {p.item_id?.slice(-6)}</td>
                        <td className="py-3 px-4 font-semibold text-charcoal-800">₹{(p.amount / 100).toLocaleString()}</td>
                        <td className="py-3 px-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${p.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                            {p.status}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-xs text-charcoal-500">{new Date(p.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'subscribers' && (
          <div className="bg-white rounded-xl shadow-md border border-charcoal-100 p-6">
            <h2 className="text-xl font-heading text-charcoal-800 mb-6">Newsletter Subscribers</h2>
            {subscribers.length === 0 ? (
              <p className="text-center text-charcoal-500 py-12">No subscribers yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-charcoal-100">
                      <th className="text-left py-3 px-4 font-body font-semibold text-charcoal-700">Email</th>
                      <th className="text-left py-3 px-4 font-body font-semibold text-charcoal-700">Name</th>
                      <th className="text-left py-3 px-4 font-body font-semibold text-charcoal-700">Subscribed</th>
                      <th className="text-right py-3 px-4 font-body font-semibold text-charcoal-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscribers.map((s) => (
                      <tr key={s.email} className="border-b border-charcoal-50">
                        <td className="py-3 px-4 text-charcoal-800">{s.email}</td>
                        <td className="py-3 px-4 text-charcoal-600">{s.name || '—'}</td>
                        <td className="py-3 px-4 text-xs text-charcoal-500">{new Date(s.subscribed_at).toLocaleDateString()}</td>
                        <td className="py-3 px-4 text-right">
                          <button onClick={() => handleUnsubscribe(s.email)} className="text-red-600 hover:text-red-500">
                            <Trash2 size={18} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'contacts' && (
          <div className="bg-white rounded-xl shadow-md border border-charcoal-100 p-6">
            <h2 className="text-xl font-heading text-charcoal-800 mb-6">Contact Inquiries</h2>
            {contacts.length === 0 ? (
              <p className="text-center text-charcoal-500 py-12">No inquiries yet.</p>
            ) : (
              <div className="space-y-4">
                {contacts.map((c) => (
                  <div key={c.id} className="border border-charcoal-100 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="font-medium text-charcoal-800">{c.name}</div>
                        <div className="text-sm text-charcoal-500">{c.email} {c.phone ? `• ${c.phone}` : ''}</div>
                      </div>
                      <div className="text-xs text-charcoal-500">{new Date(c.submitted_at).toLocaleString()}</div>
                    </div>
                    <p className="text-sm text-charcoal-700 mt-2">{c.message}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'settings' && <SiteSettingsEditor />}
        {activeTab === 'users' && <UserManagement currentUser={user} />}
        {activeTab === 'gallery-media' && <GalleryManagement />}

        {activeTab === 'donations' && (
          <div className="bg-white rounded-xl shadow-md border border-charcoal-100 p-6">
            <h2 className="text-xl font-heading text-charcoal-800 mb-6">Donations Received</h2>
            {donations.length === 0 ? (
              <p className="text-center text-charcoal-500 py-12">No donations yet.</p>
            ) : (
              <>
                {/* Desktop / tablet — table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-charcoal-100">
                        <th className="text-left py-3 px-4 font-body font-semibold text-charcoal-700">Donor</th>
                        <th className="text-left py-3 px-4 font-body font-semibold text-charcoal-700">Amount</th>
                        <th className="text-left py-3 px-4 font-body font-semibold text-charcoal-700">PAN</th>
                        <th className="text-left py-3 px-4 font-body font-semibold text-charcoal-700">80G</th>
                        <th className="text-left py-3 px-4 font-body font-semibold text-charcoal-700">Status</th>
                        <th className="text-left py-3 px-4 font-body font-semibold text-charcoal-700">Date</th>
                        <th className="text-left py-3 px-4 font-body font-semibold text-charcoal-700">Receipt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {donations.map((d) => (
                        <tr key={d.order_id} className="border-b border-charcoal-50">
                          <td className="py-3 px-4">
                            <div className="font-medium text-charcoal-800">{d.donor_name}</div>
                            <div className="text-xs text-charcoal-500">{d.donor_email}</div>
                          </td>
                          <td className="py-3 px-4 font-semibold text-saffron-700">₹{(d.amount / 100).toLocaleString()}</td>
                          <td className="py-3 px-4 text-xs text-charcoal-500">{d.pan_number || '—'}</td>
                          <td className="py-3 px-4 text-xs">
                            {d.wants_80g_receipt ? <span className="bg-saffron-100 text-saffron-700 px-2 py-1 rounded-full">Yes</span> : '—'}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${d.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                              {d.status}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-xs text-charcoal-500">{new Date(d.created_at).toLocaleDateString()}</td>
                          <td className="py-3 px-4">
                            {d.status === 'completed' ? (
                              <a
                                href={`${API_URL}/api/donations/receipt/${d.order_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`admin-download-receipt-${d.order_id}`}
                                className="inline-flex items-center gap-1 text-saffron-600 hover:text-saffron-700 text-xs font-medium"
                              >
                                <Download size={14} /> PDF
                              </a>
                            ) : (
                              <span className="text-xs text-charcoal-400">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile — card list */}
                <div className="md:hidden space-y-3" data-testid="donations-mobile-list">
                  {donations.map((d) => (
                    <div
                      key={d.order_id}
                      data-testid={`donation-card-${d.order_id}`}
                      className="border border-charcoal-100 rounded-xl p-4 bg-cream/40"
                    >
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-charcoal-800 truncate">{d.donor_name}</div>
                          <div className="text-xs text-charcoal-500 truncate">{d.donor_email}</div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-heading font-semibold text-saffron-700 text-lg leading-none">
                            ₹{(d.amount / 100).toLocaleString()}
                          </div>
                          <div className="text-[10px] text-charcoal-500 mt-1">
                            {new Date(d.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-semibold ${d.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                          {d.status}
                        </span>
                        {d.wants_80g_receipt && (
                          <span className="bg-saffron-100 text-saffron-700 px-2.5 py-1 rounded-full text-[10px] font-semibold">
                            80G
                          </span>
                        )}
                        {d.pan_number && (
                          <span className="text-[10px] text-charcoal-500 font-mono">PAN: {d.pan_number}</span>
                        )}
                      </div>
                      {d.status === 'completed' && (
                        <a
                          href={`${API_URL}/api/donations/receipt/${d.order_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`admin-download-receipt-mobile-${d.order_id}`}
                          className="inline-flex items-center gap-1.5 bg-saffron-600 text-white rounded-full px-3 py-1.5 text-xs font-medium hover:bg-saffron-500 transition-colors"
                        >
                          <Download size={13} /> Download PDF Receipt
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex justify-between items-center p-6 border-b border-charcoal-100 sticky top-0 bg-white z-10">
              <h3 className="text-2xl font-heading text-charcoal-800">
                {editingItem ? 'Edit' : 'Add'} {activeTabDef?.label?.replace(/s$/, '')}
              </h3>
              <button onClick={closeModal} className="text-charcoal-500 hover:text-charcoal-700">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {activeTab === 'courses' && (
                <>
                  <input type="text" placeholder="Title *" required value={formData.title || ''} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" />
                  <textarea placeholder="Description *" required rows={3} value={formData.description || ''} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" />
                  <input type="text" placeholder="Category *" required value={formData.category || ''} onChange={(e) => setFormData({ ...formData, category: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" />
                  <ImageUpload label="Course Image *" recommendedSize="1200×630 (16:9)" value={formData.image_url} onChange={(v) => setFormData({ ...formData, image_url: v })} testId="course-image" />
                  <input type="url" placeholder="Preview Video URL (YouTube embed, optional)" value={formData.preview_video_url || ''} onChange={(e) => setFormData({ ...formData, preview_video_url: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" />
                  <div className="grid md:grid-cols-2 gap-3">
                    <input type="text" placeholder="Duration (e.g., 8 weeks)" value={formData.duration || ''} onChange={(e) => setFormData({ ...formData, duration: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" data-testid="course-duration" />
                    <input type="text" placeholder="Level (e.g., Foundation, All Seekers)" value={formData.level || ''} onChange={(e) => setFormData({ ...formData, level: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" data-testid="course-level" />
                  </div>
                  <KeyFeaturesInput
                    placeholder="One feature per line (e.g., Live sessions with Guruji)"
                    value={formData.key_features}
                    onChange={(arr) => setFormData((prev) => ({ ...prev, key_features: arr }))}
                    testId="course-key-features"
                  />
                  <PricingModeInput formData={formData} setFormData={setFormData} />
                  {(formData.pricing_mode === 'fee' || formData.pricing_mode === 'dakshina') && (
                    <div className="bg-saffron-50/50 border border-saffron-100 rounded-lg p-4 space-y-2">
                      <label className="flex items-center gap-2 text-sm text-charcoal-700">
                        <input type="checkbox" checked={formData.allow_partial_payment || false} onChange={(e) => setFormData({ ...formData, allow_partial_payment: e.target.checked })} data-testid="course-partial-toggle" />
                        <span>Allow partial / installment payments</span>
                      </label>
                      {formData.allow_partial_payment && (
                        <input
                          type="number"
                          placeholder="Minimum partial payment (₹)"
                          min="0"
                          step="1"
                          value={((formData.min_partial_amount || 0) / 100) || ''}
                          onChange={(e) => setFormData({ ...formData, min_partial_amount: Math.round(parseFloat(e.target.value || '0') * 100) })}
                          className="w-full px-4 py-2 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                          data-testid="course-min-partial"
                        />
                      )}
                    </div>
                  )}
                </>
              )}
              {activeTab === 'events' && (
                <>
                  <input type="text" placeholder="Title *" required value={formData.title || ''} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" />
                  <textarea placeholder="Description *" required rows={3} value={formData.description || ''} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" />
                  <input type="date" required value={formData.event_date || ''} onChange={(e) => setFormData({ ...formData, event_date: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" />
                  <input type="time" required value={formData.event_time || ''} onChange={(e) => setFormData({ ...formData, event_time: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" />
                  <input type="text" placeholder="Location *" required value={formData.location || ''} onChange={(e) => setFormData({ ...formData, location: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" />
                  <ImageUpload label="Event Image *" recommendedSize="1200×630 (16:9)" value={formData.image_url} onChange={(v) => setFormData({ ...formData, image_url: v })} testId="event-image" />
                  <label className="flex items-center gap-2"><input type="checkbox" checked={formData.is_online || false} onChange={(e) => setFormData({ ...formData, is_online: e.target.checked })} /><span className="text-charcoal-700">Online Event</span></label>
                  <div className="grid md:grid-cols-2 gap-3">
                    <input type="text" placeholder="Duration (e.g., 3 hours)" value={formData.duration || ''} onChange={(e) => setFormData({ ...formData, duration: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" data-testid="event-duration" />
                    <input type="text" placeholder="Level (e.g., Open to All)" value={formData.level || ''} onChange={(e) => setFormData({ ...formData, level: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" data-testid="event-level" />
                  </div>
                  <KeyFeaturesInput
                    placeholder="One feature per line (e.g., Group meditation)"
                    value={formData.key_features}
                    onChange={(arr) => setFormData((prev) => ({ ...prev, key_features: arr }))}
                    testId="event-key-features"
                  />
                  <PricingModeInput formData={formData} setFormData={setFormData} />
                  {(formData.pricing_mode === 'fee' || formData.pricing_mode === 'dakshina') && (
                    <div className="bg-saffron-50/50 border border-saffron-100 rounded-lg p-4 space-y-2">
                      <label className="flex items-center gap-2 text-sm text-charcoal-700">
                        <input type="checkbox" checked={formData.allow_partial_payment || false} onChange={(e) => setFormData({ ...formData, allow_partial_payment: e.target.checked })} data-testid="event-partial-toggle" />
                        <span>Allow partial / installment payments</span>
                      </label>
                      {formData.allow_partial_payment && (
                        <input
                          type="number"
                          placeholder="Minimum partial payment (₹)"
                          min="0"
                          step="1"
                          value={((formData.min_partial_amount || 0) / 100) || ''}
                          onChange={(e) => setFormData({ ...formData, min_partial_amount: Math.round(parseFloat(e.target.value || '0') * 100) })}
                          className="w-full px-4 py-2 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                          data-testid="event-min-partial"
                        />
                      )}
                    </div>
                  )}
                </>
              )}
              {activeTab === 'satsangs' && (
                <>
                  <input type="text" placeholder="Title *" required value={formData.title || ''} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" />
                  <textarea placeholder="Description *" required rows={3} value={formData.description || ''} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" />
                  <input type="date" required value={formData.date || ''} onChange={(e) => setFormData({ ...formData, date: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" />
                  <input type="time" required value={formData.time || ''} onChange={(e) => setFormData({ ...formData, time: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" />
                  <input type="text" placeholder="Speaker *" required value={formData.speaker || ''} onChange={(e) => setFormData({ ...formData, speaker: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" />
                  <ImageUpload label="Satsang Image *" recommendedSize="1200×630 (16:9)" value={formData.image_url} onChange={(v) => setFormData({ ...formData, image_url: v })} testId="satsang-image" />
                  <PricingModeInput formData={formData} setFormData={setFormData} />
                </>
              )}
              {activeTab === 'resources' && (
                <>
                  <input type="text" placeholder="Title *" required value={formData.title || ''} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" />
                  <textarea placeholder="Content *" required rows={4} value={formData.content || ''} onChange={(e) => setFormData({ ...formData, content: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" />
                  <input type="text" placeholder="Category *" required value={formData.category || ''} onChange={(e) => setFormData({ ...formData, category: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" />
                  <ImageUpload label="Resource Image (optional)" recommendedSize="800×450 (16:9)" value={formData.image_url} onChange={(v) => setFormData({ ...formData, image_url: v })} testId="resource-image" />
                </>
              )}
              {activeTab === 'testimonials' && (
                <>
                  <input type="text" placeholder="Student Name *" required value={formData.name || ''} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" />
                  <input type="text" placeholder="Role / Course *" required value={formData.role || ''} onChange={(e) => setFormData({ ...formData, role: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" />
                  <textarea placeholder="Testimonial Quote *" required rows={4} value={formData.quote || ''} onChange={(e) => setFormData({ ...formData, quote: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" />
                  <ImageUpload label="Student Photo (optional)" recommendedSize="400×400 (1:1 square)" value={formData.image_url} onChange={(v) => setFormData({ ...formData, image_url: v })} testId="testimonial-image" />
                  <div className="flex items-center gap-4">
                    <label className="text-charcoal-700 font-medium">Rating:</label>
                    <select value={formData.rating || 5} onChange={(e) => setFormData({ ...formData, rating: parseInt(e.target.value) })} className="px-4 py-2 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500">
                      <option value={5}>★★★★★ (5)</option>
                      <option value={4}>★★★★ (4)</option>
                      <option value={3}>★★★ (3)</option>
                    </select>
                  </div>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={formData.is_featured || false} onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })} /><span className="text-charcoal-700">Featured</span></label>
                </>
              )}
              {activeTab === 'videos' && (
                <>
                  <input type="text" placeholder="Video Title *" required value={formData.title || ''} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" />
                  <textarea placeholder="Description *" required rows={3} value={formData.description || ''} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" />
                  <input type="text" placeholder="Category *" required value={formData.category || ''} onChange={(e) => setFormData({ ...formData, category: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" />
                  <div>
                    <label className="block text-sm font-body font-medium text-charcoal-700 mb-1.5">YouTube Embed URL *</label>
                    <p className="text-xs text-charcoal-500 italic mb-2">Use format: https://www.youtube.com/embed/VIDEO_ID (not the watch URL)</p>
                    <input type="url" placeholder="https://www.youtube.com/embed/abc123" required value={formData.video_url || ''} onChange={(e) => setFormData({ ...formData, video_url: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" />
                  </div>
                  <ImageUpload label="Thumbnail Image (optional)" recommendedSize="1280×720 (16:9)" value={formData.thumbnail_url} onChange={(v) => setFormData({ ...formData, thumbnail_url: v })} testId="video-thumbnail" />
                </>
              )}
              {activeTab === 'publications' && (
                <>
                  <input type="text" placeholder="Title *" required value={formData.title || ''} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" data-testid="pub-title" />
                  <select required value={formData.category || 'Chakra Energy'} onChange={(e) => setFormData({ ...formData, category: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" data-testid="pub-category">
                    <option value="Chakra Energy">Chakra Energy</option>
                    <option value="Mind Harmonization">Mind Harmonization</option>
                    <option value="Dosha Cleansing">Dosha Cleansing</option>
                    <option value="Vibrational Science">Vibrational Science</option>
                  </select>
                  <input type="text" placeholder="Lead Guidance (e.g., Guruji Risheeshwar Narendranji)" value={formData.lead_guidance || ''} onChange={(e) => setFormData({ ...formData, lead_guidance: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" />
                  <textarea placeholder="Team Members — one per line" rows={2} value={(formData.team_members || []).join('\n')} onChange={(e) => setFormData({ ...formData, team_members: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean) })} className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" />
                  <input type="text" placeholder="Publish Date (e.g., March 2026)" value={formData.publish_date || ''} onChange={(e) => setFormData({ ...formData, publish_date: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" />
                  <textarea placeholder="Abstract" rows={3} value={formData.abstract || ''} onChange={(e) => setFormData({ ...formData, abstract: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" />
                  <textarea placeholder="Scriptural Reference" rows={2} value={formData.scriptural_reference || ''} onChange={(e) => setFormData({ ...formData, scriptural_reference: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" />
                  <textarea placeholder="Findings" rows={3} value={formData.findings || ''} onChange={(e) => setFormData({ ...formData, findings: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" />
                  <ImageUpload label="Thumbnail Image (optional)" recommendedSize="1200×630 (16:9)" value={formData.thumbnail_url} onChange={(v) => setFormData({ ...formData, thumbnail_url: v })} testId="pub-thumbnail" />
                  <PdfUpload label="Research Paper (PDF)" value={formData.pdf_url} onChange={(v) => setFormData({ ...formData, pdf_url: v })} testId="pub-pdf" />
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={formData.is_featured || false} onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })} />
                    <span className="text-charcoal-700">Feature as case study</span>
                  </label>
                </>
              )}
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={closeModal} className="flex-1 border border-charcoal-200 text-charcoal-700 rounded-lg px-6 py-3 hover:bg-charcoal-50 transition-colors">Cancel</button>
                <button type="submit" disabled={saving} data-testid="admin-form-submit" className="flex-1 bg-saffron-600 text-white rounded-lg px-6 py-3 hover:bg-saffron-500 transition-colors disabled:opacity-60 disabled:cursor-wait">{saving ? 'Saving…' : editingItem ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};
